// src/Dashboard/admin/components/ui.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";

export function SelectField({ label, value, onChange, options, disabled }) {
  return (
    <div className="min-w-0">
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
        {label}
      </label>
      <div className="relative min-w-0">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none px-3 pr-9 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm disabled:opacity-60"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
      </div>
    </div>
  );
}

/**
 * ComboboxField (searchable select)
 * options: [{ value, label, subLabel? }]
 */
export function ComboboxField({
  label,
  value,
  onChange,
  options = [],
  disabled,
  placeholder = "Select…",
  searchPlaceholder = "Type to search…",
  emptyText = "No matches.",
}) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const selected = useMemo(
    () => (options || []).find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const filtered = useMemo(() => {
    const query = String(q || "").trim().toLowerCase();
    if (!query) return options || [];
    return (options || []).filter((o) => {
      const a = String(o.label || "").toLowerCase();
      const b = String(o.subLabel || "").toLowerCase();
      const v = String(o.value || "").toLowerCase();
      return a.includes(query) || b.includes(query) || v.includes(query);
    });
  }, [options, q]);

  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  return (
    <div className="min-w-0" ref={ref}>
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
        {label}
      </label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border text-sm min-w-0 ${
          disabled
            ? "opacity-60 cursor-not-allowed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950"
            : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900"
        }`}
      >
        <div className="min-w-0 text-left">
          <div className="truncate font-medium">
            {selected?.label || placeholder}
          </div>
          {selected?.subLabel ? (
            <div className="truncate text-[11px] text-slate-500">
              {selected.subLabel}
            </div>
          ) : null}
        </div>
        <ChevronDown size={16} className="text-slate-400 shrink-0" />
      </button>

      {open && !disabled ? (
        <div className="relative">
          <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="p-2 border-b border-slate-100 dark:border-slate-800">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-500/30"
              />
            </div>

            <div className="max-h-64 overflow-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">{emptyText}</div>
              ) : (
                filtered.map((o) => {
                  const active = String(o.value) === String(value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => {
                        onChange(String(o.value));
                        setOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60 ${
                        active
                          ? "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200"
                          : ""
                      }`}
                    >
                      <div className="text-sm font-semibold truncate">{o.label}</div>
                      {o.subLabel ? (
                        <div className="text-[11px] text-slate-500 truncate">
                          {o.subLabel}
                        </div>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function StatCard({ icon: Icon, title, value }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-5">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-500/10 shrink-0">
          <Icon size={20} className="text-orange-600 dark:text-orange-300" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 truncate">{title}</div>
          <div className="text-2xl font-semibold truncate">{value}</div>
        </div>
      </div>
    </div>
  );
}

export function AnalyticsBar({ label, value, total, suffix = "" }) {
  const pct = total > 0 ? Math.round((Number(value || 0) / Number(total || 1)) * 100) : 0;
  const safePct = Math.min(100, Math.max(0, pct));

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-1 gap-3 min-w-0">
        <div className="text-xs text-slate-600 dark:text-slate-300 truncate">{label}</div>
        <div className="text-xs text-slate-500 shrink-0">
          {value}
          {suffix} <span className="text-slate-400">({safePct}%)</span>
        </div>
      </div>
      <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className="h-full bg-orange-600 dark:bg-orange-400" style={{ width: `${safePct}%` }} />
      </div>
    </div>
  );
}

export function Badge({ tone = "neutral", icon: Icon, label }) {
  const styles =
    tone === "success"
      ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200 border-green-200 dark:border-green-800"
      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700";

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-semibold ${styles}`}
    >
      {Icon ? <Icon size={14} /> : null}
      {label}
    </span>
  );
}

export function AvatarInitials({ name }) {
  const initials =
    String(name || "")
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((x) => x[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "U";

  return (
    <div className="w-10 h-10 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 flex items-center justify-center font-semibold shrink-0">
      {initials}
    </div>
  );
}

export function SearchSection({ title, empty, items, renderItem }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden min-w-0">
      <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
        {title}
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {items.length === 0 ? (
          <div className="px-3 py-3 text-sm text-slate-500">{empty}</div>
        ) : (
          items.map((x, idx) => <div key={idx}>{renderItem(x)}</div>)
        )}
      </div>
    </div>
  );
}

export function PlaceholderCard({ title, icon: Icon, lines }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
      <div className="flex items-center gap-3 mb-3 min-w-0">
        <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-500/10 shrink-0">
          <Icon size={20} className="text-orange-600 dark:text-orange-300" />
        </div>
        <h2 className="text-lg font-semibold truncate">{title}</h2>
      </div>
      <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1 list-disc pl-5">
        {lines.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
    </div>
  );
}

export function MiniStatusCard({ title, value, subtitle, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 shrink-0">
          <Icon size={18} className="text-slate-700 dark:text-slate-200" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 truncate">{title}</div>
          <div className="text-base font-semibold truncate">{String(value)}</div>
          <div className="text-xs text-slate-400 truncate">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

export function RowStat({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm min-w-0">
      <span className="text-slate-600 dark:text-slate-300 truncate">{label}</span>
      <span className="font-semibold shrink-0">{value}</span>
    </div>
  );
}

export function ModalShell({ title, subtitle, onClose, footer, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col min-w-0">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3 min-w-0">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{title}</div>
            {subtitle ? <div className="text-xs text-slate-500 truncate">{subtitle}</div> : null}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto overflow-x-hidden min-w-0 flex-1">{children}</div>

        <div className="p-5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
          {footer}
        </div>
      </div>
    </div>
  );
}
